import { defineStore } from "pinia";
import type { WebModelGetCompany } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { getAllCompanies } from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import { sortBy } from "~/helpers/sortBy";

interface State {
  companies: WebModelGetCompany[]; // all public companies that the user can see
}

export const useCompaniesStore = defineStore("companies", {
  // convert to a function
  state: (): State => ({
    companies: [], // initial state
  }),
  getters: {
    getAllContributorsCompaniesWithAtLeastOneAppInHub:
      (state) =>
      (hubName: string): WebModelGetCompany[] => {
        return state.companies
          .filter(
            (company) => company.approvedContributor && company.hasReleasedApp,
          )
          .filter((company) =>
            company.hubs?.some(
              (hub) => (hub.name ?? "").toLowerCase() === hubName,
            ),
          )
          .sort(sortBy({ key: "name", order: "asc" }));
      },
    getCompanyById:
      (state) =>
      (companyId: string): WebModelGetCompany | undefined => {
        return state.companies.find((company) => company.id === companyId);
      },
    companyIsActiveHub:
      (state) =>
      (hubName: string, companyId: string): boolean => {
        const company = state.companies.find((c) => c.id === companyId);
        if (!company) return false;
        if (company.hubs?.some((hub) => hub.name === hubName)) {
          return true;
        }
        return false;
      },
    getCompaniesForMultiSelect:
      (state) =>
      (hubName: string): Array<{ value: string; text: string }> => {
        return state.companies
          .filter(
            (company) => company.approvedContributor && company.hasReleasedApp,
          )
          .filter((company) =>
            company.hubs?.some(
              (hub) => (hub.name ?? "").toLowerCase() === hubName,
            ),
          )
          .map((company) => ({
            value: company.id,
            text: company.name,
          }))
          .sort(sortBy({ key: "companyName", order: "asc" }));
      },
  },
  actions: {
    async loadCompanies() {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getAllCompanies())),
      );
      console.debug("loadCompanies: ", data, error);
      if (data) {
        this.companies = data;
      }
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
